import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { formatDateTime } from '@/lib/format';
import { GuardSuggestion } from '@/components/GuardSuggestion';
import { UserSaveFooter } from './UserSaveFooter';
import {
  USER_TYPE_FIELD_DESCRIPTION,
  USER_TYPE_OPTIONS,
  hasBusinessFields,
  type UserForm,
  type UserRecord,
} from './useUserForm';

/** Date of birth is editable at most once per 365 days (loyalty.md 4.2). */
function dobLocked(updatedAt: string | null | undefined): boolean {
  if (!updatedAt) return false;
  const at = new Date(updatedAt).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at < 365 * 24 * 60 * 60 * 1000;
}

export interface UserInformationTabProps {
  user: UserRecord;
  form: UserForm;
  onCancel: () => void;
}

/** Who this person is: identity, personal profile, Exact relation, consent. */
export function UserInformationTab({
  user,
  form,
  onCancel,
}: UserInformationTabProps) {
  const { values, set } = form;
  const dobIsLocked =
    dobLocked(user.personal?.dateOfBirthUpdatedAt) &&
    Boolean(user.personal?.dateOfBirth);

  return (
    <FormLayout
      onSubmit={form.submit}
      footer={<UserSaveFooter saving={form.saving} onCancel={onCancel} />}
    >
      <FormSection title="Identity">
        <FormField label="First name">
          <Input
            value={values.firstName}
            onChange={(event) => set('firstName', event.target.value)}
          />
        </FormField>
        <FormField label="Last name">
          <Input
            value={values.lastName}
            onChange={(event) => set('lastName', event.target.value)}
          />
        </FormField>
        <FormField
          label="Email"
          required
          description="The only login identifier."
        >
          <Input
            type="email"
            value={values.email}
            onChange={(event) => set('email', event.target.value)}
            required
          />
        </FormField>
        <GuardSuggestion
          guard={form.guards.email}
          idPrefix="admin.users.email"
          disabled={form.saving}
          onAnswer={(address) => form.resubmit('email', address)}
        />
        <FormField
          label="Type"
          description={USER_TYPE_FIELD_DESCRIPTION}
        >
          <Select
            value={values.type}
            onChange={(event) => set('type', event.target.value)}
            options={USER_TYPE_OPTIONS}
          />
        </FormField>
      </FormSection>

      <FormSection
        title="Personal"
        description="Used for the door, for emails, and for completeness checks."
      >
        <FormField
          label="Date of birth"
          description={
            dobIsLocked
              ? 'Date of birth can only be changed once every 365 days.'
              : 'Editable at most once per 365 days after the first write.'
          }
        >
          <Input
            type="date"
            value={values.dateOfBirth}
            onChange={(event) => set('dateOfBirth', event.target.value)}
            disabled={dobIsLocked}
          />
        </FormField>
        <FormField label="Phone">
          <Input
            type="tel"
            value={values.phone}
            onChange={(event) => set('phone', event.target.value)}
          />
        </FormField>
        <FormField label="Address">
          <Input
            value={values.address}
            onChange={(event) => set('address', event.target.value)}
          />
        </FormField>
        <FormField label="Postal code">
          <Input
            value={values.postalCode}
            onChange={(event) => set('postalCode', event.target.value)}
          />
        </FormField>
        <FormField label="City">
          <Input
            value={values.city}
            onChange={(event) => set('city', event.target.value)}
          />
        </FormField>
        <FormField label="Country">
          <Input
            value={values.country}
            onChange={(event) => set('country', event.target.value)}
          />
        </FormField>
        <FormField label="Gender">
          <Input
            value={values.gender}
            onChange={(event) => set('gender', event.target.value)}
          />
        </FormField>
        <FormField label="Pronouns">
          <Input
            value={values.pronouns}
            onChange={(event) => set('pronouns', event.target.value)}
          />
        </FormField>
      </FormSection>

      {hasBusinessFields(values.type) ? (
        <FormSection
          title="Business"
          description="Contractor Exact Online relation (iban, KvK, VAT). Written through updateUser; Exact sync runs after commit and cannot roll this back."
        >
          <FormField label="IBAN">
            <Input
              value={values.iban}
              onChange={(event) => set('iban', event.target.value)}
            />
          </FormField>
          <FormField label="Chamber of commerce (KvK)">
            <Input
              value={values.chamberOfCommerce}
              onChange={(event) => set('chamberOfCommerce', event.target.value)}
            />
          </FormField>
          <FormField label="VAT number">
            <Input
              value={values.vatNumber}
              onChange={(event) => set('vatNumber', event.target.value)}
            />
          </FormField>
          <FormField label="Invoice email">
            <Input
              type="email"
              value={values.invoiceEmail}
              onChange={(event) => set('invoiceEmail', event.target.value)}
            />
          </FormField>
          <GuardSuggestion
          guard={form.guards.invoiceEmail}
          idPrefix="admin.users.invoiceEmail"
          disabled={form.saving}
          onAnswer={(address) => form.resubmit('invoiceEmail', address)}
        />
        </FormSection>
      ) : null}

      <FormSection
        title="Marketing"
        description="Consent is collected in ExpoApp, checkout, and imports — not editable here."
      >
        <FormField label="Marketing opt-in">
          <Badge tone={user.marketingOptIn ? 'success' : 'neutral'}>
            {user.marketingOptIn ? 'Opted in' : 'Opted out'}
          </Badge>
        </FormField>
        <FormField label="Last opted in">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {formatDateTime(user.marketingOptInAt)}
          </p>
        </FormField>
        <FormField label="Email bounced">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {formatDateTime(user.emailBouncedAt)}
          </p>
        </FormField>
      </FormSection>
    </FormLayout>
  );
}
